import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';
import {
  Breadcrumbs,
  BreadcrumbsItem,
  Card,
  CardContent,
  CardDetail,
  CardIcon,
  CardLabel,
  CardRow,
  CardValue,
  IgnoredChecksBreakdown,
  PageHeader,
  PageHeaderContent,
  PageTitle,
  Section,
  SectionHeader,
  SplitChecksToggleBreakdown,
  StatusPayment,
  Text,
  ToggleableBreakdown,
} from 'components';
import { lineHeight, theme, toRem } from 'design';
import { PaymentMethodAccountNumber } from 'forms/PaymentMethodForm/styles';
import { routeConstants } from 'routes';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getUserPaymentsRequest, getUserPayoutCalculationsRequest, Payout } from 'store/Payout';
import { IStore } from 'store/types';
import { IPayout, IUserPayoutCalculations, PayoutRefType, PayoutStatus } from 'types';
import { buildEmployeeOverrideTicketBreakdown, centsToDollarsDisplay, displayWithholding, getPayoutStatusDisplayProps, sortPayoutsByDisbursementMethod } from 'utils';
import { sortAlphaNumerically } from 'utils/arrays';

const StyledPageTitle = styled(PageTitle)`
  align-items: center;

  @media (min-width: ${theme.breakpoint.small}px) {
    align-items: start;
  }
`;

const NotesWrapper = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  gap: ${toRem(theme.space.sm)};
  margin-bottom: ${toRem(theme.space.xl)};

  header {
    margin-bottom: 0;
  }
`;

const EmployeePayoutWrapper = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  gap: ${toRem(theme.space.lg)};

  header {
    margin-bottom: 0;
  }
`;

const EmployeePayoutReport = styled.div`
  display: flex;
  flex-flow: column;
  width: 100%;
  background: ${theme.color.neutral[50]};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${theme.borderRadius.small}px;
  padding: ${toRem(theme.space.lg)};

  > * {
    margin-bottom: ${toRem(theme.space.lg)};

    &:last-child {
      margin: 0;
    }
  }
`;

const StyledCardRow = styled(CardRow)`
  grid-template-columns: 1fr;

  @media (min-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const PayoutStatusItem = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
`;

const PaymentStatusWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.xs)};

  p {
    font-size: ${toRem(theme.font.size.xxs)};
    line-height: ${lineHeight(theme.font.size.xxs)};
    color: ${theme.color.neutral[500]};
  }
`;

const PaymentMethodWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.xs)};
  width: 100%;
`;

const StyledStatusPayment = styled(StatusPayment)`
  div {
    font-size: ${toRem(theme.font.size.xxs)};
    line-height: ${lineHeight(theme.font.size.xxs)};
    font-weight: ${theme.font.weight.medium};
    color: ${theme.color.neutral[500]};
    padding: ${toRem(theme.space['2xs'])} 0;
  }
`;

const StyledPaymentMethodAccountNumberWrapper = styled.div`
  svg {
    border: 1px solid ${theme.color.neutral[200]};
    border-radius: ${toRem(2)};
  }
`;

const PayoutItemNet = styled.p<{ isCanceled?: boolean }>`
  font-size: ${toRem(theme.font.size.md)};
  font-weight: ${theme.font.weight.medium};
  text-decoration: ${({ isCanceled }) => (isCanceled ? 'line-through' : 'none')};
`;

const PayoutDetails = ({ payout }: { payout: IPayout }) => {
  const { statusLabel, subStatusLabel, subStatusAppearance } = getPayoutStatusDisplayProps(payout);
  return (
    <PayoutStatusItem data-cy="payout-status-item" id={payout.id}>
      <PaymentMethodWrapper>
        <PaymentStatusWrapper>
          <StyledStatusPayment status={statusLabel} data-cy="status-payment" subStatusLabel={subStatusLabel} subStatusAppearance={subStatusAppearance} />
          <Text size="small" color={theme.color.neutral[500]}>
            ID: {payout.id}
          </Text>
          {payout.withholdings?.employee?.amount > 0 && (
            <Text size="small" color={theme.color.neutral[500]} data-cy="withheld-amount">
              {displayWithholding(payout.withholdings?.employee?.amount)}
            </Text>
          )}
        </PaymentStatusWrapper>
        {payout?.paymentDestination && (
          <StyledPaymentMethodAccountNumberWrapper>
            <PaymentMethodAccountNumber short={true} fontSize="small" cardType={payout?.paymentDestination?.network} cardNumber={payout?.paymentDestination?.last4} dataCy="payment-method-account-number" />
          </StyledPaymentMethodAccountNumberWrapper>
        )}
      </PaymentMethodWrapper>
      <PayoutItemNet data-cy="payout-card-amount" isCanceled={payout.status === PayoutStatus.CANCELED}>
        {centsToDollarsDisplay(payout.net)}
      </PayoutItemNet>
    </PayoutStatusItem>
  );
};

const EmployeePayoutDetails = () => {
  const dispatch = useDispatch();
  const params = useParams<{ paymentId: string }>();

  // User
  const [payouts, setPayouts] = useState<IPayout[]>([]);
  const [payoutCalculations, setPayoutCalculations] = useState<IUserPayoutCalculations>();
  const isPaymentLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payout.GET_USER_PAYMENTS_REQUEST));

  // Payout Details
  const businessDate = dayjs(payouts[0]?.paymentDate).format('MM-DD-YYYY');
  const payoutSubmittedDate = dayjs(payouts[0]?.createdAt).local().format('MM-DD-YYYY');
  const payoutSubmittedTime = dayjs(payouts[0]?.createdAt).local().format('HH:mm A z');

  // Component State
  const isCalculatorPayout = payouts[0]?.refType === PayoutRefType.CALCULATOR;

  useEffect(() => {
    const onSuccess = (payouts: IPayout[]) => setPayouts(sortPayoutsByDisbursementMethod(payouts));
    if (params?.paymentId) dispatch(getUserPaymentsRequest(params?.paymentId, onSuccess));
  }, []);

  useEffect(() => {
    if (isCalculatorPayout && payouts?.length && payoutCalculations?.payoutId !== payouts[0]?.id) dispatch(getUserPayoutCalculationsRequest(payouts[0]?.id, setPayoutCalculations));
  }, [payouts]);

  const totalNet = useMemo(() => payouts?.filter((p) => p.status !== PayoutStatus.CANCELED).reduce((acc, payout) => acc + (payout?.net || 0), 0) || 0, [payouts]);
  const calcResults = payoutCalculations?.results;

  const renderSegmentBreakdown = useMemo(
    () =>
      calcResults?.map((segment) => {
        const calculations = segment.employees.filter((c) => c.received.length || c.contributions.length || c.retained?.total > 0);

        if (!calculations?.length) return <></>;

        return calculations
          .filter((r) => r.calculated.total > 0 || r.totals.totalMinWorked > 0)
          .map((calculation, idx) => (
            <ToggleableBreakdown
              key={`segment-breakdown-${idx}`}
              categoryGroups={segment.meta.categoryGroups}
              calculatorRules={segment.rules}
              calculation={calculation}
              segmentName={segment.segment}
              creditCardFee={segment.creditCardPercentFee}
            />
          ));
      }),
    [calcResults]
  );

  const renderOverrideBreakdown = useMemo(() => {
    if (!calcResults) return null;

    const { splitChecks, ignoredChecks } = buildEmployeeOverrideTicketBreakdown(calcResults.flatMap((result) => result.employees));

    return (
      <>
        {(!!splitChecks.received?.length || !!splitChecks.contributions?.length) && <SplitChecksToggleBreakdown splitChecks={splitChecks} />}
        {!!ignoredChecks.length && <IgnoredChecksBreakdown ignoredCheckData={ignoredChecks.sort(sortAlphaNumerically('displayId'))} />}
      </>
    );
  }, [calcResults]);

  return (
    <>
      <PageHeader data-cy="page-header" hasStats>
        <Breadcrumbs data-cy="breadcrumbs">
          <BreadcrumbsItem label="Dashboard" to={routeConstants.private.rootPath} />
          <BreadcrumbsItem label="Payment Details" />
        </Breadcrumbs>
        <PageHeaderContent stackable>
          <StyledPageTitle data-cy="payment-details-total" label="Total" title={centsToDollarsDisplay(totalNet)} isLoading={isPaymentLoading} />
        </PageHeaderContent>
        <StyledCardRow data-cy="info-card-row">
          <Card>
            <CardIcon icon="Site" />
            <CardContent data-cy="site-card">
              <CardLabel isLoading={isPaymentLoading}>Site</CardLabel>
              <CardValue isLoading={isPaymentLoading}>{payouts[0]?.site?.name}</CardValue>
            </CardContent>
          </Card>
          <Card>
            <CardIcon icon="Calendar" />
            <CardContent data-cy="submitted-date-card">
              <CardLabel isLoading={isPaymentLoading}>Submitted</CardLabel>
              <CardValue isLoading={isPaymentLoading}>{payoutSubmittedDate}</CardValue>
              <CardDetail isLoading={isPaymentLoading}>{payoutSubmittedTime}</CardDetail>
            </CardContent>
          </Card>
          <Card>
            <CardIcon icon="Calendar" />
            <CardContent data-cy="business-date-card">
              <CardLabel isLoading={isPaymentLoading}>Business Date</CardLabel>
              <CardValue isLoading={isPaymentLoading}>{businessDate}</CardValue>
            </CardContent>
          </Card>
        </StyledCardRow>
      </PageHeader>

      <Section afterHeaderCardRow>
        {!isPaymentLoading && payouts[0]?.notes && (
          <NotesWrapper>
            <SectionHeader title="Note" isLoading={isPaymentLoading} />
            {!isPaymentLoading && payouts[0]?.notes ? payouts[0]?.notes : '--'}
          </NotesWrapper>
        )}

        <EmployeePayoutWrapper>
          <SectionHeader title="Details" isLoading={isPaymentLoading} />
          <EmployeePayoutReport data-cy="payout-calculation-breakdown-report">
            {payouts?.map((payout) => (
              <PayoutDetails key={payout.id} payout={payout} />
            ))}
            {renderSegmentBreakdown}
            {renderOverrideBreakdown}
          </EmployeePayoutReport>
        </EmployeePayoutWrapper>
      </Section>
    </>
  );
};

export default EmployeePayoutDetails;
